/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import { css, StyleSheet } from 'aphrodite';
import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router';
import { colors } from '@blend/consumer-components';

import { notaryAPI } from '../../../api/notary';
import { NotaryAPI } from '../../../api/notary/types';
import { NavigationSidebar } from '../../../components/common/NavigationSidebar/NavigationSidebar';
import { pageSize } from '../../../components/common/Pipeline/constants';
import { usePermissions } from '../../../../src/hooks/usePermissions';
import { AssignmentsPipeline } from './AssignmentPipeline';
import { defaultPipelineFilters } from '@utils/filters';
import { convertURLSearchToFilterParams, updateUrlSearch } from '@utils/filters';
import { FilterParams } from '@shared/types/pipeline';

const styles = StyleSheet.create({
  wrapper: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    height: '100%',
    backgroundColor: colors.snowPlus1,
  },
  pipeline: {
    width: '100%',
    height: '100%',
    overflow: 'scroll',
  },
});

export namespace Assignments {
  export interface Props {}
}

export const Assignments: React.FC<Assignments.Props> = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [rooms, setRooms] = useState<NotaryAPI.RoomListItem[]>([]);
  const [hasError, setHasError] = useState<boolean>(false);
  const [totalCount, setTotalCount] = useState<number>(0);
  const hasSuperAdminPermission = usePermissions().includes(NotaryAPI.BSRPermissions.BSRSuperAdmin);
  const [filters, setFilters] = useState(defaultPipelineFilters(hasSuperAdminPermission));
  const [page, setNewPage] = useState<number>(defaultPipelineFilters(hasSuperAdminPermission).page);
  const history = useHistory();
  const location = useLocation();
  const search = location.search.slice(1);

  const fetchRooms = async (filter: FilterParams) => {
    try {
      setIsLoading(true);
      const result = await notaryAPI.rooms.fetchAssignedRooms(pageSize, filter.page * pageSize, filter);
      setRooms(result.rooms);
      setTotalCount(result.totalCount);
    } catch (error) {
      setHasError(true);
    } finally {
      setIsLoading(false);
    }
  };

  const setPage = (page: number) => {
    setNewPage(page);
    const params = new URLSearchParams(location.search.slice(1));
    params.set('page', page.toString());
    history.push(`${location.pathname}?${params.toString()}`);
  };

  useEffect(() => {
    (async () => {
      const url = new URL(location.pathname, window.location.origin);
      let urlFilters = defaultPipelineFilters(hasSuperAdminPermission);
      if (location.search.length > 0) {
        urlFilters = convertURLSearchToFilterParams(location.search.slice(1), hasSuperAdminPermission);
      } else {
        updateUrlSearch(url, urlFilters);

        const pathname = location.pathname.endsWith('/') ? location.pathname.slice(0, -1) : location.pathname;
        history.replace(`${pathname}?${url.searchParams.toString()}`);
        return;
      }

      updateUrlSearch(url, urlFilters);
      setNewPage(() => urlFilters.page);
      // if location.pathname ends with / then remove it
      const pathname = location.pathname.endsWith('/') ? location.pathname.slice(0, -1) : location.pathname;
      history.replace(`${pathname}?${url.searchParams.toString()}`);
      await fetchRooms(urlFilters);
    })();
  }, [search]);

  return (
    <div className={css(styles.wrapper)}>
      <NavigationSidebar />
      <div className={css(styles.pipeline)}>
        <AssignmentsPipeline
          data-it="AssignmentsPipeline"
          isLoading={isLoading}
          rooms={rooms}
          hasError={hasError}
          page={page}
          totalCount={totalCount}
          fetchRooms={fetchRooms}
          setPage={setPage}
          filters={filters}
          setFilters={setFilters}
        />
      </div>
    </div>
  );
};
